import { Ticket } from '@data/tickets/model/ticket'

// Who acted on a ticket, and when a real user last did. Deliberately free of React so it can be
// unit-tested directly.
//
// Each function prefers the small summary fields requested from the API (`actionedBy`,
// `hasHistory`) and falls back to walking `history` while the API still sends it. The list
// endpoint's `history` is 98.4% of a 64 MB response (docs/ticket-list-payload-api-request.md),
// so these are the only places allowed to read it. The fallbacks are the pre-existing logic,
// moved here unchanged, so behaviour is the same until the API ships.

// Distinct names of users who actioned the ticket, current version first, then history.
// `excludeIds` is applied here rather than by the API: ticket cards include the system user
// and the details view and list view exclude it.
export const getActionedByUsers = (ticket: Ticket, excludeIds: number[] = []): string[] => {
  const userNames = new Set<string>()
  const excludedUserIds = new Set(excludeIds)

  if (ticket.actionedBy) {
    for (const actor of ticket.actionedBy) {
      if (!excludedUserIds.has(actor.userIdRef) && actor.userName) {
        userNames.add(actor.userName)
      }
    }

    return Array.from(userNames)
  }

  if (ticket.userIdRef && !excludedUserIds.has(ticket.userIdRef) && ticket.userName) {
    userNames.add(ticket.userName)
  }

  if (ticket.history && ticket.history.length > 0) {
    ticket.history.forEach(historyItem => {
      if (historyItem.userIdRef && !excludedUserIds.has(historyItem.userIdRef)) {
        if (historyItem.userName) {
          userNames.add(historyItem.userName)
        }
      }
    })
  }

  return Array.from(userNames)
}

// The latest event date, as epoch ms, among versions made by a user not in `excludeIds`. Used
// for the list view's sortable "last user event" column.
export const getLastUserEventAt = (ticket: Ticket, excludeIds: number[] = []): number | undefined => {
  const excludedUserIds = new Set(excludeIds)

  const timestamps = ticket.actionedBy
    ? ticket.actionedBy
      .filter(actor => actor.userIdRef && !excludedUserIds.has(actor.userIdRef))
      .map(actor => new Date(actor.lastEventDate).getTime())
    : [ticket, ...(ticket.history ?? [])]
      .filter(event => event.userIdRef && !excludedUserIds.has(event.userIdRef))
      .map(event => new Date(event.eventDate).getTime())

  const valid = timestamps.filter(timestamp => !Number.isNaN(timestamp))

  return valid.length > 0 ? Math.max(...valid) : undefined
}

export const ticketHasHistory = (ticket: Ticket): boolean =>
  ticket.hasHistory ?? Boolean(ticket.history?.length)
